import { ComponentProps } from 'react';
import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { Divider, Tabs, Text } from '@belong/ui';
import clsx from 'clsx';
import { AgreeToTerms } from 'src/features/auth/components/agree-to-terms/agree-to-terms';
import { ExternalLogin } from 'src/features/auth/components/external-login/external-login';
import { RegionPhoneNumber } from 'src/features/auth/components/region-phone-number/region-phone-number';
import { SignUpForm } from 'src/features/auth/components/sign-up-form/sign-up-form';
import { AUTH_STRINGS } from 'src/features/auth/strings';
import css from './sign-up-tab-panel.module.css';

type Props = Partial<{
  enforcedEmail?: string;
  onError:
    | ComponentProps<typeof SignUpForm>['onError']
    | ComponentProps<typeof ExternalLogin>['onError'];
  onExternalLoginSuccess: ComponentProps<typeof ExternalLogin>['onSuccess'];
  onExternalLoginSuccessWithoutPhoneOrEmail: ComponentProps<
    typeof ExternalLogin
  >['onSuccessWithoutPhoneOrEmail'];
  onFinish:
    | ComponentProps<typeof SignUpForm>['onFinish']
    | ComponentProps<typeof ExternalLogin>['onFinish'];
  onSignUpSuccess: ComponentProps<typeof SignUpForm>['onSuccess'];
  onStart:
    | ComponentProps<typeof SignUpForm>['onStart']
    | ComponentProps<typeof ExternalLogin>['onStart'];
  shouldAskForPassword?: boolean;
  shouldAskForSmsConsent?: boolean;
}>;

export function SignUpTabPanel({
  enforcedEmail,
  onError = () => {},
  onExternalLoginSuccess = async () => {},
  onExternalLoginSuccessWithoutPhoneOrEmail = async () => {},
  onFinish = async () => {},
  onSignUpSuccess = async () => {},
  onStart = () => {},
  shouldAskForPassword = false,
  shouldAskForSmsConsent = false,
}: Props) {
  const { width } = useWindowSize();

  const isMobile = width < BREAKPOINTS_WIDTHS.SM;
  const shouldShowExternalLogin = !enforcedEmail;

  return (
    <Tabs.PanelItem>
      <div className="mb-2xl">
        <div className="sm:mt-sm sm:flex sm:justify-center">
          {shouldShowExternalLogin && (
            <>
              <div
                className={clsx(
                  'my-sm sm:my-xs sm:flex sm:flex-col sm:items-center sm:justify-center',
                  css.panel,
                )}
              >
                <ExternalLogin
                  onError={onError}
                  onFinish={onFinish}
                  onStart={onStart}
                  onSuccess={onExternalLoginSuccess}
                  onSuccessWithoutPhoneOrEmail={
                    onExternalLoginSuccessWithoutPhoneOrEmail
                  }
                />
                {shouldAskForSmsConsent ? null : (
                  <div className="mt-lg hidden text-center sm:block">
                    <AgreeToTerms />
                  </div>
                )}
              </div>
              <div className="mb-xs mt-lg px-lg w-full sm:m-0 sm:w-auto">
                <Divider
                  className={clsx(!isMobile && css.divider)}
                  direction={isMobile ? 'horizontal' : 'vertical'}
                >
                  <div className="mx-xs sm:my-xs">
                    <Text className="text-gray" fontWeight="semibold">
                      {AUTH_STRINGS['or']}
                    </Text>
                  </div>
                </Divider>
              </div>
            </>
          )}
          <div
            className={clsx(
              'sm:flex sm:items-center',
              css.panel,
              shouldAskForSmsConsent && css.panelWithConsent,
            )}
          >
            <SignUpForm
              onError={onError}
              onFinish={onFinish}
              onStart={onStart}
              onSuccess={onSignUpSuccess}
              shouldAskForPassword={shouldAskForPassword}
              shouldAskForSmsConsent={shouldAskForSmsConsent}
              enforcedEmail={enforcedEmail}
            />
          </div>
        </div>
        {!shouldAskForSmsConsent && (
          <div className="my-lg text-center sm:hidden">
            <AgreeToTerms />
          </div>
        )}
        <div className="mt-lg flex w-full items-center justify-center">
          <RegionPhoneNumber />
        </div>
      </div>
    </Tabs.PanelItem>
  );
}
